import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';
import { 
  Loader2, ArrowLeft, Building2, User, Mail, Phone, Globe, 
  Calendar, DollarSign, Users, Briefcase, FileText, ExternalLink,
  CheckCircle, XCircle, Clock, Star, Brain, MessageSquare, ChevronDown, ChevronUp
} from 'lucide-react';
import { applicationsAPI, evaluatorsAPI } from '../services/api';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';

const AdminApplicationReview = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { language } = useLanguage();
  const { isAdmin, user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [application, setApplication] = useState(null);
  const [newStatus, setNewStatus] = useState('');
  const [notes, setNotes] = useState('');
  const [evaluationScores, setEvaluationScores] = useState([]);
  const [expandedEvaluators, setExpandedEvaluators] = useState({});

  const t = {
    es: {
      title: 'Revisar Postulación',
      back: 'Volver al Dashboard',
      program: 'Desafío',
      status: 'Estado',
      submittedOn: 'Enviada el',
      startupInfo: 'Información de la Startup',
      startupName: 'Nombre',
      website: 'Sitio Web',
      description: 'Descripción',
      stage: 'Etapa',
      teamSize: 'Tamaño del Equipo',
      fundingRaised: 'Financiamiento Recibido',
      founderInfo: 'Información del Fundador',
      founderName: 'Nombre',
      email: 'Email',
      phone: 'Teléfono',
      documents: 'Documentos',
      pitchDeck: 'Pitch Deck',
      noDocuments: 'No hay documentos adjuntos',
      updateStatus: 'Actualizar Estado',
      selectStatus: 'Seleccionar estado',
      notes: 'Notas internas',
      notesPlaceholder: 'Agregar notas sobre esta postulación...',
      save: 'Guardar Cambios',
      saving: 'Guardando...',
      stages: {
        idea: 'Idea',
        mvp: 'MVP',
        early_stage: 'Etapa Temprana',
        growth: 'Crecimiento',
        scale: 'Escala'
      },
      statuses: {
        submitted: 'Enviada',
        under_review: 'En Revisión',
        finalist: 'Finalista',
        accepted: 'Aceptada',
        rejected: 'Rechazada'
      },
      successTitle: '¡Éxito!',
      successDesc: 'Estado actualizado correctamente.',
      errorTitle: 'Error',
      errorDesc: 'No se pudo actualizar el estado.',
      notAllowed: 'No tienes permiso para ver esta página.'
    },
    en: {
      title: 'Review Application',
      back: 'Back to Dashboard',
      program: 'Challenge',
      status: 'Status',
      submittedOn: 'Submitted on',
      startupInfo: 'Startup Information',
      startupName: 'Name',
      website: 'Website',
      description: 'Description',
      stage: 'Stage',
      teamSize: 'Team Size',
      fundingRaised: 'Funding Raised',
      founderInfo: 'Founder Information',
      founderName: 'Name',
      email: 'Email',
      phone: 'Phone',
      documents: 'Documents',
      pitchDeck: 'Pitch Deck',
      noDocuments: 'No documents attached',
      updateStatus: 'Update Status',
      selectStatus: 'Select status',
      notes: 'Internal Notes',
      notesPlaceholder: 'Add notes about this application...',
      save: 'Save Changes',
      saving: 'Saving...',
      stages: {
        idea: 'Idea',
        mvp: 'MVP',
        early_stage: 'Early Stage',
        growth: 'Growth',
        scale: 'Scale'
      },
      statuses: {
        submitted: 'Submitted',
        under_review: 'Under Review',
        finalist: 'Finalist',
        accepted: 'Accepted',
        rejected: 'Rejected'
      },
      successTitle: 'Success!',
      successDesc: 'Status updated successfully.',
      errorTitle: 'Error',
      errorDesc: 'Could not update status.',
      notAllowed: 'You do not have permission to view this page.'
    }
  }[language];

  useEffect(() => {
    const isAllowed = isAdmin() || user?.role === 'organization';
    if (!isAllowed) {
      toast({
        title: t.errorTitle,
        description: t.notAllowed,
        variant: 'destructive'
      });
      navigate(-1);
      return;
    }
    fetchApplication();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchApplication = async () => {
    try {
      setLoading(true);
      const data = await applicationsAPI.getById(id);
      setApplication(data);
      setNewStatus(data.status);
      setNotes(data.notes || '');
      // Fetch evaluation scores
      try {
        const scores = await evaluatorsAPI.getDetailedScores(id);
        setEvaluationScores(scores);
      } catch (e) {
        console.error('Error fetching scores:', e);
      }
    } catch (error) {
      console.error('Error fetching application:', error);
      toast({
        title: t.errorTitle,
        description: t.errorDesc,
        variant: 'destructive'
      });
      navigate(-1);
    } finally {
      setLoading(false);
    }
  };

  const toggleEvaluator = (evalId) => {
    setExpandedEvaluators(prev => ({ ...prev, [evalId]: !prev[evalId] }));
  };

  const handleStatusUpdate = async () => {
    try {
      setSaving(true);
      
      await applicationsAPI.updateStatus(id, {
        status: newStatus,
        notes: notes
      });

      toast({
        title: t.successTitle,
        description: t.successDesc,
      });

      // Refresh data
      fetchApplication();
    } catch (error) {
      console.error('Error updating status:', error);
      toast({
        title: t.errorTitle,
        description: error.response?.data?.detail || t.errorDesc,
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  const getStatusBadgeStyle = (status) => {
    switch (status) {
      case 'accepted':
        return 'bg-green-100 text-green-800 border-green-300';
      case 'finalist':
        return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'under_review':
        return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'rejected':
        return 'bg-red-100 text-red-800 border-red-300';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'accepted':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'rejected':
        return <XCircle className="w-5 h-5 text-red-600" />;
      default:
        return <Clock className="w-5 h-5 text-yellow-600" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex justify-center items-center">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <Button 
            variant="ghost" 
            onClick={() => navigate(-1)}
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t.back}
          </Button>
          
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{t.title}</h1>
              <p className="text-gray-600 mt-1">
                {t.program}: <span className="font-semibold">{application?.programName}</span>
              </p>
            </div>
            <div className="text-right">
              <Badge className={`${getStatusBadgeStyle(application?.status)} text-sm px-3 py-1`}>
                {t.statuses[application?.status] || application?.status}
              </Badge>
              <p className="text-sm text-gray-500 mt-2">
                {t.submittedOn}: {new Date(application?.submittedDate).toLocaleDateString()}
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Startup Information */}
            <Card className="border-2 border-gray-200">
              <CardHeader className="border-b">
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-purple-600" />
                  {t.startupInfo}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">{t.startupName}</p>
                    <p className="font-semibold text-gray-900">{application?.startupName}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">{t.stage}</p>
                    <p className="font-semibold text-gray-900">
                      {t.stages[application?.stage] || application?.stage}
                    </p>
                  </div>
                </div>

                {application?.website && (
                  <div>
                    <p className="text-sm text-gray-500">{t.website}</p>
                    <a 
                      href={application.website} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-purple-600 hover:underline flex items-center gap-1"
                    >
                      {application.website}
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  </div>
                )}

                <div>
                  <p className="text-sm text-gray-500">{t.description}</p>
                  <p className="text-gray-700 whitespace-pre-wrap">{application?.description}</p>
                </div>

                <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                  <div className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="text-sm text-gray-500">{t.teamSize}</p>
                      <p className="font-semibold">{application?.teamSize} people</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="text-sm text-gray-500">{t.fundingRaised}</p>
                      <p className="font-semibold">
                        ${application?.fundingRaised?.toLocaleString() || 0}
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Founder Information */}
            <Card className="border-2 border-gray-200">
              <CardHeader className="border-b">
                <CardTitle className="flex items-center gap-2">
                  <User className="w-5 h-5 text-purple-600" />
                  {t.founderInfo}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="flex items-center gap-3">
                    <User className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="text-sm text-gray-500">{t.founderName}</p>
                      <p className="font-semibold">{application?.founderName}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Mail className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="text-sm text-gray-500">{t.email}</p>
                      <a 
                        href={`mailto:${application?.email}`}
                        className="font-semibold text-purple-600 hover:underline"
                      >
                        {application?.email}
                      </a>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Phone className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="text-sm text-gray-500">{t.phone}</p>
                      <p className="font-semibold">{application?.phone}</p>
                    </div>
                  </div>
                </div>
                
                {/* Show submitter info if different from founder */}
                {application?.userEmail && application.userEmail !== application.email && (
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <p className="text-sm text-gray-500 mb-2">
                      {language === 'es' ? 'Enviado por (usuario registrado):' : 'Submitted by (registered user):'}
                    </p>
                    <a 
                      href={`mailto:${application.userEmail}`}
                      className="text-purple-600 hover:underline"
                    >
                      {application.userEmail}
                    </a>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Documents */}
            <Card className="border-2 border-gray-200">
              <CardHeader className="border-b">
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-purple-600" />
                  {t.documents}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                {application?.pitchDeck ? (
                  <a 
                    href={`${process.env.REACT_APP_BACKEND_URL}${application.pitchDeck}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 p-4 border-2 border-gray-200 rounded-lg hover:border-purple-300 transition-colors"
                  >
                    <FileText className="w-8 h-8 text-purple-600" />
                    <div>
                      <p className="font-semibold text-gray-900">{t.pitchDeck}</p>
                      <p className="text-sm text-gray-500">{language === 'es' ? 'Clic para descargar' : 'Click to download'}</p>
                    </div>
                    <ExternalLink className="w-5 h-5 text-gray-400 ml-auto" />
                  </a>
                ) : (
                  <p className="text-gray-500 text-center py-4">{t.noDocuments}</p>
                )}
              </CardContent>
            </Card>

            {/* Evaluation Results */}
            {evaluationScores.length > 0 && (
              <Card className="border-2 border-gray-200" data-testid="evaluation-results-card">
                <CardHeader className="border-b">
                  <CardTitle className="flex items-center gap-2">
                    <Star className="w-5 h-5 text-amber-500" />
                    {language === 'es' ? 'Resultados de Evaluación' : 'Evaluation Results'}
                    <Badge variant="secondary" className="ml-2 text-xs">
                      {evaluationScores.length} {language === 'es' ? 'evaluaciones' : 'evaluations'}
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6 space-y-4">
                  {/* Summary bar */}
                  {(() => {
                    const avg = evaluationScores.reduce((sum, s) => sum + (s.weightedScore || 0), 0) / evaluationScores.length;
                    return (
                      <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg" data-testid="eval-summary">
                        <div className="text-center">
                          <p className="text-3xl font-bold text-gray-900">{avg.toFixed(1)}</p>
                          <p className="text-xs text-gray-500 uppercase tracking-wider">{language === 'es' ? 'Prom. Ponderado' : 'Weighted Avg'}</p>
                        </div>
                        <div className="flex-1 h-3 bg-gray-200 rounded-full overflow-hidden">
                          <div className="h-full rounded-full transition-all" style={{ width: `${Math.min(100, avg)}%`, backgroundColor: avg >= 70 ? '#16a34a' : avg >= 40 ? '#d97706' : '#dc2626' }} />
                        </div>
                        <span className="text-sm font-semibold text-gray-600">{avg.toFixed(1)}%</span>
                      </div>
                    );
                  })()}

                  {/* Evaluator cards */}
                  {evaluationScores.map((evalScore, idx) => {
                    const isExpanded = expandedEvaluators[evalScore._id] !== false;
                    const isAI = evalScore.is_ai;
                    const scores = evalScore.scores || [];
                    const aiTitle = isAI && evalScore.ai_profile_title ? (evalScore.ai_profile_title[language] || evalScore.ai_profile_title.es || '') : '';

                    return (
                      <div
                        key={evalScore._id || idx}
                        className={`border-2 rounded-lg overflow-hidden transition-colors ${isAI ? 'border-amber-200' : 'border-gray-200'}`}
                        data-testid={`eval-card-${idx}`}
                      >
                        {/* Evaluator header */}
                        <button
                          className={`w-full flex items-center justify-between p-4 text-left transition-colors ${isAI ? 'bg-amber-50 hover:bg-amber-100' : 'bg-gray-50 hover:bg-gray-100'}`}
                          onClick={() => toggleEvaluator(evalScore._id)}
                          data-testid={`eval-toggle-${idx}`}
                        >
                          <div className="flex items-center gap-3">
                            {isAI && evalScore.ai_profile_avatar ? (
                              <img src={evalScore.ai_profile_avatar} alt="" className="w-10 h-10 rounded-full object-cover border-2 border-amber-300" />
                            ) : (
                              <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                                <User className="w-5 h-5 text-purple-600" />
                              </div>
                            )}
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="font-semibold text-gray-900">{evalScore.evaluatorName || 'Evaluador'}</span>
                                {isAI && (
                                  <Badge className="bg-amber-100 text-amber-800 border-amber-300 text-[10px] px-1.5 py-0">
                                    <Brain className="w-3 h-3 mr-0.5" /> IA
                                  </Badge>
                                )}
                              </div>
                              {aiTitle && <p className="text-xs text-gray-500">{aiTitle}</p>}
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="text-right">
                              <p className="text-lg font-bold text-gray-900">{(evalScore.weightedScore || 0).toFixed(1)}%</p>
                              <p className="text-[10px] text-gray-400 uppercase">{language === 'es' ? 'Puntaje' : 'Score'}</p>
                            </div>
                            {isExpanded ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
                          </div>
                        </button>

                        {/* Expanded content */}
                        {isExpanded && (
                          <div className="p-4 space-y-3 border-t border-gray-100">
                            {/* Criterion scores */}
                            {scores.map((criterion, ci) => {
                              const maxScore = 10;
                              const pct = Math.min(100, (criterion.score / maxScore) * 100);
                              const comment = criterion.justification || criterion.comment || '';
                              return (
                                <div key={ci} className="space-y-1" data-testid={`criterion-${idx}-${ci}`}>
                                  <div className="flex items-center justify-between">
                                    <span className="text-sm font-medium text-gray-700">{criterion.criterionName}</span>
                                    <span className="text-sm font-bold text-gray-900">{criterion.score}/{maxScore}</span>
                                  </div>
                                  <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                                    <div
                                      className="h-full rounded-full transition-all"
                                      style={{ width: `${pct}%`, backgroundColor: pct >= 70 ? '#16a34a' : pct >= 40 ? '#d97706' : '#dc2626' }}
                                    />
                                  </div>
                                  {comment && (
                                    <div className="flex items-start gap-2 mt-1 pl-2 border-l-2 border-gray-200">
                                      <MessageSquare className="w-3.5 h-3.5 text-gray-400 mt-0.5 shrink-0" />
                                      <p className="text-xs text-gray-600 leading-relaxed">{comment}</p>
                                    </div>
                                  )}
                                </div>
                              );
                            })}

                            {/* Overall comment */}
                            {(evalScore.overall_comment || evalScore.overallComment) && (
                              <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-100" data-testid={`eval-comment-${idx}`}>
                                <p className="text-xs font-semibold text-blue-700 uppercase tracking-wider mb-1">
                                  {language === 'es' ? 'Comentario General' : 'Overall Comment'}
                                </p>
                                <p className="text-sm text-gray-700 leading-relaxed">{evalScore.overall_comment || evalScore.overallComment}</p>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar - Status Update */}
          <div className="space-y-6">
            <Card className="border-2 border-purple-200 bg-purple-50">
              <CardHeader className="border-b border-purple-200">
                <CardTitle className="flex items-center gap-2 text-purple-900">
                  {getStatusIcon(application?.status)}
                  {t.updateStatus}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-4">
                <div>
                  <Label>{t.status}</Label>
                  <Select 
                    value={newStatus} 
                    onValueChange={setNewStatus}
                  >
                    <SelectTrigger className="bg-white">
                      <SelectValue placeholder={t.selectStatus} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="submitted">{t.statuses.submitted}</SelectItem>
                      <SelectItem value="under_review">{t.statuses.under_review}</SelectItem>
                      <SelectItem value="finalist">{t.statuses.finalist}</SelectItem>
                      <SelectItem value="accepted">{t.statuses.accepted}</SelectItem>
                      <SelectItem value="rejected">{t.statuses.rejected}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>{t.notes}</Label>
                  <Textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder={t.notesPlaceholder}
                    rows={4}
                    className="bg-white"
                  />
                </div>

                <Button 
                  onClick={handleStatusUpdate}
                  disabled={saving}
                  className="w-full bg-purple-600 text-white hover:bg-purple-700"
                >
                  {saving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {t.saving}
                    </>
                  ) : (
                    t.save
                  )}
                </Button>
              </CardContent>
            </Card>

            {/* Quick Stats */}
            <Card className="border-2 border-gray-200">
              <CardContent className="p-6">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500">{t.stage}</span>
                    <span className="font-semibold">
                      {t.stages[application?.stage] || application?.stage}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500">{t.teamSize}</span>
                    <span className="font-semibold">{application?.teamSize}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500">{t.fundingRaised}</span>
                    <span className="font-semibold">
                      ${application?.fundingRaised?.toLocaleString() || 0}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminApplicationReview;
